import './lib/suppressLogs'; 
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import WizardLayout from './components/onboarding/WizardLayout';
import FoxOnboarding from './components/onboarding/FoxOnboarding';
import FoxOnboarding2 from './components/onboarding/FoxOnboarding2';
import Layout from './components/Layout';
import DashboardV2 from './pages/DashboardV2';
import LeadDetail from './pages/LeadDetail';
import EnterpriseAnalytics from './pages/EnterpriseAnalytics';
import CampaignManagement from './pages/campaignManagement';
import CalendarSettings from './components/CalendarSettings';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EmailConfirmationHandler from './pages/EmailConfirmationHandler';
import ProtectedRoute from './components/ProtectedRoute';
import EnterpriseRoute from './components/EnterpriseRoute';
import GlobalAdminRoute from './components/GlobalAdminRoute';
import AffiliatesAdmin from './components/AffiliatesAdmin';
import AffiliatePortal from './pages/AffiliatePortal';
import AffiliateLogin from './pages/AffiliateLogin';
import AffiliateSetPassword from './pages/AffiliateSetPassword';
import AffiliateForgotPassword from './pages/AffiliateForgotPassword';
import AffiliateDashboard from './pages/AffiliateDashboard';
import BillingPage from './pages/BillingPage';
import SecurityAuditLogs from './pages/SecurityAuditLogs';
import AffiliateAuditLog from './pages/AffiliateAuditLog';
import A2PStatusDashboard from './pages/A2PStatusDashboard';
import CustomerHealthDashboard from './pages/CustomerHealthDashboard';
import CronHealthDashboard from './pages/CronHealthDashboard';
import OnboardingGuard from './components/OnboardingGuard';
import FinancialDashboard from './pages/FinancialDashboard';
import CostExplorer from './pages/CostExplorer';
import CustomerCosts from './pages/CustomerCosts';
import UnitEconomics from './pages/UnitEconomics';
import InvoiceManager from './pages/InvoiceManager';
import FinancialRunway from './pages/FinancialRunway';
import RateManagement from './pages/RateManagement';
import EmailManager from './pages/EmailManager';
import TosAlerts from './pages/TosAlerts';
import VoiceTrunkProvisioning from './pages/VoiceTrunkProvisioning';
import Reviewers from './pages/Reviewers';
import InvitationSignupPage from './pages/InvitationSignupPage';
import supabase from './lib/supabaseClient';
import HelpCenter from './help-v2/HelpCenter'; // Modern help system with Cmd+K search
import SetPassword from './pages/SetPassword';
import Referrals from './pages/Referrals';
import PartnerInvite from './pages/PartnerInvite';
import AIControlRoom  from './pages/AIControlRoomAdaptive/AIControlRoomAdaptive';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Settings from './pages/Settings';
import AIConfigurationHub from './components/settings/AIConfigurationHub';
import AIStudio from './components/settings/AIStudio';
import OAuthCallback from './pages/OAuthCallback';
import QCQueue from './pages/QCQueue';
import MonthlyReports from './pages/MonthlyReports';
import ExtensionAuth from './pages/ExtensionAuth';
import ConnectAuthorize from './pages/ConnectAuthorize';
import ApiDocs from './pages/ApiDocs';
import WelcomePage from './pages/WelcomePage';
import WebChatInbox from './pages/WebChatInbox';
import VoiceCallsInbox from './pages/VoiceCallsInbox';
import EscalationsDashboard from './pages/EscalationsDashboard';
import Hunter from './pages/Hunter';
import SuspendedPage from './pages/SuspendedPage';

// Component to handle root redirect with email confirmation check
function RootRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkConfirmationAndRedirect = async () => {
      console.log('🔍 RootRedirect - Checking URL params and session...');
      
      // Check for Supabase confirmation parameters in URL
      const urlParams = new URLSearchParams(window.location.search);
      const type = urlParams.get('type');
      const error = urlParams.get('error');
      const error_description = urlParams.get('error_description');
      
      // Also check hash params (Supabase sometimes uses these)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const hashType = hashParams.get('type');
      const accessToken = hashParams.get('access_token');
      
      console.log('URL params:', { type, error, hasAccessToken: !!accessToken });
      
      // Handle errors
      if (error) {
        console.error('❌ Supabase error:', error, error_description);
        navigate('/login', { 
          replace: true, 
          state: { error: error_description || error } 
        });
        return;
      }
      
      // Check if this is a confirmation callback
      if ((type === 'signup' || hashType === 'signup') && accessToken) {
        console.log('📧 Email confirmation callback detected, redirecting to handler...');
        navigate('/auth/callback' + window.location.hash + window.location.search);
        return;
      }
      
      // If we have type=signup in URL params but no access token, 
      // this might be a post-confirmation redirect
      if (type === 'signup') {
        console.log('📧 Post-email confirmation detected, checking session...');
        
        // Give Supabase a moment to establish the session
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          navigate('/login', { replace: true });
          return;
        }
        
        if (session?.user) {
          console.log('✅ User confirmed and logged in');
          
          // Check if this is an invited user
          const isInvitedUser = session.user.user_metadata?.invited_signup || 
                               session.user.user_metadata?.invitation_id;
          
          if (isInvitedUser) {
            console.log('🎯 Invited user detected, processing invitation...');
            
            // Call the edge function to process the invitation
            try {
              const { data, error: edgeError } = await supabase.functions.invoke('process-invitation', {
                body: {
                  user_id: session.user.id,
                  email: session.user.email,
                  invitation_id: session.user.user_metadata?.invitation_id || null
                }
              });

              if (edgeError) {
                console.error('❌ Edge Function error:', edgeError);
              } else {
                console.log('✅ Invitation processed successfully');
              }
            } catch (error) {
              console.error('Edge function error:', error);
            }
            
            // Navigate to dashboard for invited users
            navigate('/dashboard', { replace: true });
            return;
          } else {
            // Regular user, check onboarding. Look up tenant_id via
            // users_profile, not by `tenants.id = user.id` (that only works
            // when the user IS the founder; breaks for invited users and
            // any case where the user's auth id != the tenant's id). Fail
            // safe to /control-room if anything goes sideways — better an
            // authenticated dashboard with nav options than an onboarding
            // loop for tenants that are already past it (this is the bug
            // that locked the HealthPlus CEO out 2026-05-29).
            try {
              const { data: profile } = await supabase
                .from('users_profile')
                .select('tenant_id')
                .eq('id', session.user.id)
                .single();
              const tenantId = profile?.tenant_id;
              if (!tenantId) {
                navigate('/control-room', { replace: true });
                return;
              }
              const { data: tenant } = await supabase
                .from('tenants')
                .select('onboarding_complete')
                .eq('id', tenantId)
                .single();
              if (tenant?.onboarding_complete === false) {
                navigate('/onboarding', { replace: true });
              } else {
                navigate('/control-room', { replace: true });
              }
            } catch (error) {
              console.error('[RootRedirect] onboarding-complete check failed; falling back to control-room', error);
              navigate('/control-room', { replace: true });
            }
            return;
          }
        }
      }

      // Normal redirect logic - no confirmation params
      console.log('🔄 Normal redirect logic...');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        console.log('👤 User logged in:', session.user.email);
        
        // Check if invited user
        const isInvitedUser = session.user.user_metadata?.invited_signup || 
                             session.user.user_metadata?.invitation_id;
        
        if (isInvitedUser) {
          navigate('/dashboard', { replace: true });
        } else {
          // Check onboarding status — same fix as the email-confirmation
          // branch above: look up tenant_id via users_profile, fail safe to
          // /control-room. Only route to /onboarding when we have positive
          // confirmation that onboarding_complete is literally false.
          try {
            const { data: profile } = await supabase
              .from('users_profile')
              .select('tenant_id')
              .eq('id', session.user.id)
              .single();
            const tenantId = profile?.tenant_id;
            if (!tenantId) {
              navigate('/control-room', { replace: true });
              return;
            }
            const { data: tenant } = await supabase
              .from('tenants')
              .select('onboarding_complete')
              .eq('id', tenantId)
              .single();
            if (tenant?.onboarding_complete === false) {
              navigate('/onboarding', { replace: true });
            } else {
              navigate('/control-room', { replace: true });
            }
          } catch (error) {
            console.error('[RootRedirect] onboarding-complete check failed; falling back to control-room', error);
            navigate('/control-room', { replace: true });
          }
        }
      } else {
        console.log('❌ No user session, redirecting to login');
        navigate('/login', { replace: true });
      }
    };

    checkConfirmationAndRedirect();
  }, [navigate, location]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>  {/* 👈 ADD THIS WRAPPER */}
      <Router>
        <Routes>
        {/* Standalone routes for login and signup */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} /> {/* ADD THIS LINE */}
        <Route path="/onboarding" element={<FoxOnboarding2 />} />
        <Route path="/onboarding-v2" element={<FoxOnboarding2 />} />
        <Route path="/onboarding-v1" element={<FoxOnboarding />} />
        <Route path="/onboarding/legacy" element={<WizardLayout />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/auth/callback" element={<EmailConfirmationHandler />} />
        <Route path="/auth/confirm" element={<EmailConfirmationHandler />} />
        <Route path="/invitation-signup" element={<InvitationSignupPage />} />
        <Route path="/extension-auth" element={<ExtensionAuth />} />
        {/* AI assistant consent screen. OUTSIDE ProtectedRoute on purpose: it keeps its own return path through /login (see the file header). */}
        <Route path="/connect/authorize" element={<ConnectAuthorize />} />
        <Route path="/api-docs" element={<ApiDocs />} />
        <Route path="/suspended" element={<SuspendedPage />} />
        {/* Affiliate self-serve login (declared before the dynamic :token route below,
            which is a legacy no-login link kept working for anyone already holding one) */}
        <Route path="/affiliate/login" element={<AffiliateLogin />} />
        <Route path="/affiliate/set-password" element={<AffiliateSetPassword />} />
        <Route path="/affiliate/forgot-password" element={<AffiliateForgotPassword />} />
        <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
        {/* Public read-only affiliate dashboard, token-gated (no login) */}
        <Route path="/affiliate/:token" element={<AffiliatePortal />} />

        {/* Protected Routes wrapped by Layout and OnboardingGuard */}
        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/welcome" element={<WelcomePage />} />
          
          <Route
            path="/control-room"
            element={
              <OnboardingGuard>
                <Layout>
                  <AIControlRoom />
                </Layout>
              </OnboardingGuard>
            }
          />
          
<Route
  path="/dashboard"
  element={
    <OnboardingGuard>
      <Layout>
        <DashboardV2 />
      </Layout>
    </OnboardingGuard>
  }
/>
<Route
  path="/ai-studio"
  element={
    <OnboardingGuard>
      <Layout>
        <AIStudio />
      </Layout>
    </OnboardingGuard>
  }
/>

          <Route
            path="/billing"
            element={
              <OnboardingGuard>
                <Layout>
                  <BillingPage />
                </Layout>
              </OnboardingGuard>
            } 
          />
          <Route 
            path="/referrals" 
            element={
              <OnboardingGuard>
                <Layout>
                  <Referrals />
                </Layout>
              </OnboardingGuard>
            } 
          />
          {/* Enterprise Analytics - only for global_admin and enterprise_admin */}
          <Route 
            path="/enterprise-analytics" 
            element={
              <OnboardingGuard>
                <Layout>
                  <EnterpriseRoute>
                    <EnterpriseAnalytics />
                  </EnterpriseRoute>
                </Layout>
              </OnboardingGuard>
            } 
          />

          
          {/* Campaign Management - for admin roles */}
          <Route 
            path="/campaign-management" 
            element={
              <OnboardingGuard>
                <Layout>
                  <CampaignManagement />
                </Layout>
              </OnboardingGuard>
            } 
          />
          
          {/* Calendar Settings - global_admin only */}
          <Route 
            path="/calendar-settings" 
            element={
              <OnboardingGuard>
                <Layout>
                  <CalendarSettings />
                </Layout>
              </OnboardingGuard>
            } 
          />
          <Route
  path="/qc-queue"
  element={
    <OnboardingGuard>
      <Layout>
        <QCQueue />
      </Layout>
    </OnboardingGuard>
  }
/>
          <Route
            path="/monthly-reports"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <MonthlyReports />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          {/* Security Audit Logs - global_admin only */}
          <Route
            path="/security-audit"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <SecurityAuditLogs />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          {/* Affiliate Sign-In Activity - global_admin only. Separate from
              Security Audit Logs on purpose: affiliates aren't in auth.users,
              so their auth events live in a separate table (affiliate_auth_events),
              not security_audit_logs. */}
          <Route
            path="/admin/affiliate-audit"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <AffiliateAuditLog />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          {/* A2P Registration Status - global_admin only */}
          <Route
            path="/admin/a2p"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <A2PStatusDashboard />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          {/* Customer Health Dashboard - global_admin only */}
          <Route
            path="/admin/customer-health"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <CustomerHealthDashboard />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          {/* Cron Health Monitor - global_admin only */}
          <Route
            path="/admin/cron-health"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <CronHealthDashboard />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          {/* Affiliates - invite-only referral partners - global_admin only */}
          <Route
            path="/admin/affiliates"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <AffiliatesAdmin />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          {/* Hunter - signal prospecting console - global_admin only */}
          <Route
            path="/admin/hunter"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <Hunter />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          {/* Financial Intelligence System - global_admin only */}
          <Route
            path="/admin/financials"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <FinancialDashboard />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />
          <Route
            path="/admin/financials/explorer"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <CostExplorer />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />
          <Route
            path="/admin/financials/customers/:tenant_id?"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <CustomerCosts />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />
          <Route
            path="/admin/financials/unit-economics"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <UnitEconomics />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />
          <Route
            path="/admin/financials/invoices"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <InvoiceManager />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          <Route
            path="/admin/financials/runway"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <FinancialRunway />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          <Route
            path="/admin/financials/rates"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <RateManagement />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          <Route
            path="/admin/email-manager"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <EmailManager />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          <Route
            path="/admin/tos-alerts"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <TosAlerts />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          <Route
            path="/admin/voice-trunk-provisioning"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <VoiceTrunkProvisioning />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          <Route
            path="/admin/reviewers"
            element={
              <OnboardingGuard>
                <Layout>
                  <GlobalAdminRoute>
                    <Reviewers />
                  </GlobalAdminRoute>
                </Layout>
              </OnboardingGuard>
            }
          />

          <Route path="/oauth-callback" element={<OAuthCallback />} />
          <Route path="/partner-invite" 
          element={
            <OnboardingGuard>
              <Layout>
                <PartnerInvite />
              </Layout>
            </OnboardingGuard>
          } />
          <Route 
            path="/help" 
            element={
              <OnboardingGuard>
                <Layout>
                  <HelpCenter />
                </Layout>
              </OnboardingGuard>
            } 
          />

          <Route 
            path="/settings" 
            element={
              <OnboardingGuard>
                <Layout>
                  <Settings />
                </Layout>
              </OnboardingGuard>
            } 
          />
          <Route
            path="/lead/:id"
            element={
              <OnboardingGuard>
                <Layout>
                  <LeadDetail />
                </Layout>
              </OnboardingGuard>
            }
          />
          <Route
            path="/escalations"
            element={
              <GlobalAdminRoute>
                <Layout>
                  <EscalationsDashboard />
                </Layout>
              </GlobalAdminRoute>
            }
          />
          <Route
            path="/web-chat"
            element={
              <OnboardingGuard>
                <Layout>
                  <WebChatInbox />
                </Layout>
              </OnboardingGuard>
            }
          />
          <Route
            path="/messenger"
            element={
              <OnboardingGuard>
                <Layout>
                  <WebChatInbox channel="messenger" />
                </Layout>
              </OnboardingGuard>
            }
          />
          <Route
            path="/voice-calls"
            element={
              <OnboardingGuard>
                <Layout>
                  <VoiceCallsInbox />
                </Layout>
              </OnboardingGuard>
            }
          />
        </Route>
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}